import React, { useState, useEffect, useMemo } from 'react';
import { GoogleMap, useLoadScript, Marker, Circle, InfoWindow } from '@react-google-maps/api';
import { DriverLocationResponse, AppState } from '../../types/drivers';

interface SearchDriversMapProps {
  centerLatitude: number;
  centerLongitude: number;
  radius: number;
  drivers: DriverLocationResponse[];
}

const containerStyle = {
  width: '100%',
  height: '600px',
  borderRadius: '8px'
};

const SearchDriversMap: React.FC<SearchDriversMapProps> = ({
  centerLatitude,
  centerLongitude,
  radius,
  drivers
}) => {
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: googleMapsApiKey,
  });

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<DriverLocationResponse | null>(null);

  const center = useMemo(() => ({
    lat: centerLatitude,
    lng: centerLongitude
  }), [centerLatitude, centerLongitude]);

  // Fit map bounds when drivers change
  useEffect(() => {
    if (!map || !isLoaded) return;

    const bounds = new window.google.maps.LatLngBounds();

    // Calculate the bounds of the search radius circle
    // 1 degree latitude = ~111km, adjust for radius in meters
    const radiusInDegrees = (radius / 1000) / 111;

    // Add points at the edges of the radius circle (north, south, east, west)
    bounds.extend({ lat: center.lat + radiusInDegrees, lng: center.lng }); // North
    bounds.extend({ lat: center.lat - radiusInDegrees, lng: center.lng }); // South
    bounds.extend({ lat: center.lat, lng: center.lng + radiusInDegrees }); // East
    bounds.extend({ lat: center.lat, lng: center.lng - radiusInDegrees }); // West

    // Also add all driver locations
    drivers.forEach(driver => {
      bounds.extend({ lat: driver.latitude, lng: driver.longitude });
    });

    // Fit map to bounds with padding
    map.fitBounds(bounds, 50);
  }, [map, isLoaded, drivers, center, radius]);

  const onMapLoad = (mapInstance: google.maps.Map) => {
    setMap(mapInstance);
  };

  const getDriverMarkerIcon = (appState: AppState) => {
    if (!isLoaded || !window.google) return undefined;

    const isOnline = appState === AppState.FOREGROUND;

    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: isOnline ? '#28a745' : '#dc3545',
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      scale: 8,
    };
  };

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  };

  if (loadError) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
        Error loading map
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
        Loading map...
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={13}
        onLoad={onMapLoad}
        options={{
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
        }}
      >
        {/* Radius Circle */}
        <Circle
          center={center}
          radius={radius}
          options={{
            fillColor: '#007bff',
            fillOpacity: 0.1,
            strokeColor: '#007bff',
            strokeOpacity: 0.4,
            strokeWeight: 2,
          }}
        />

        {/* Driver Markers */}
        {drivers.map((driver) => (
          <Marker
            key={driver.id}
            position={{ lat: driver.latitude, lng: driver.longitude }}
            title={`Driver ${driver.driverId}`}
            icon={getDriverMarkerIcon(driver.appState)}
            onClick={() => setSelectedDriver(driver)}
            zIndex={1000}
          />
        ))}

        {/* Info Window for Selected Driver */}
        {selectedDriver && (
          <InfoWindow
            position={{ lat: selectedDriver.latitude, lng: selectedDriver.longitude }}
            onCloseClick={() => setSelectedDriver(null)}
          >
            <div style={{ padding: '0.5rem' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem' }}>
                Driver #{selectedDriver.driverId}
              </h4>
              <div style={{ fontSize: '0.85rem', color: '#666' }}>
                <div><strong>Status:</strong> {selectedDriver.appState === AppState.FOREGROUND ? '🟢 Online' : '🔴 Offline'}</div>
                <div><strong>Heading:</strong> {selectedDriver.heading.toFixed(2)}°</div>
                <div><strong>Last Update:</strong> {formatDate(selectedDriver.serverTimeIntervalUTC)}</div>
                <div style={{ marginTop: '0.5rem' }}>
                  <a
                    href={`/drivers/${selectedDriver.driverId}`}
                    style={{ color: '#007bff', textDecoration: 'none' }}
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.href = `/drivers/${selectedDriver.driverId}`;
                    }}
                  >
                    View Details →
                  </a>
                </div>
              </div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* Map Legend */}
      <div style={{
        position: 'absolute',
        bottom: '20px',
        left: '20px',
        backgroundColor: 'white',
        padding: '1rem',
        borderRadius: '8px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
        fontSize: '0.9rem'
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Legend</div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
          <div style={{
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: '#28a745',
            marginRight: '8px',
            border: '2px solid white'
          }} />
          <span>Online Driver</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: '#dc3545',
            marginRight: '8px',
            border: '2px solid white'
          }} />
          <span>Offline Driver</span>
        </div>
        <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #dee2e6' }}>
          <strong>Total Drivers:</strong> {drivers.length}
        </div>
      </div>
    </div>
  );
};

export default SearchDriversMap;
